import { useEffect, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { updateComputer, updateProfile } from "./Api/EndpointApi";
import { PolicyProfiles } from "../PolicyProfiles/interface";
import ConfirmationDialog from "./ConfirmationDialog";

interface DeviceModeDropdownProps {
  device: any;
  selectedTenant: any;
  role?: string;
  fetchDeviceList?: () => void;
  profiles?: PolicyProfiles[] | [];
  disabled?: boolean;
  onModeUpdate?: (deviceId: string) => void;
}

export default function DeviceModeDropdown({
  device,
  selectedTenant,
  fetchDeviceList,
  profiles = [],
  disabled = false,
  onModeUpdate,
}: DeviceModeDropdownProps) {
  const [selectedProfile, setSelectedProfile] = useState(device.profile || "");
  const [pendingProfile, setPendingProfile] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    // Skip while a change is in flight — the profile update is a slow, multi-step backend
    // write (updateComputer, then updateProfile after a delay), and the device list keeps
    // polling in the background during that window. Syncing from the (still stale) `device.profile`
    // prop here would snap the dropdown back to the old value until the update fully lands,
    // making it look like a page reload is required to see the change.
    if (isUpdating) {
      return;
    }
    setSelectedProfile(device.profile || "");
  }, [device.profile, isUpdating]);

  const handleModeChange = (mode: string) => {
    if (mode === selectedProfile) return;
    setPendingProfile(mode);
    setShowConfirmDialog(true);
  };

  const handleConfirmChange = async () => {
    if (!pendingProfile) return;

    const previousProfile = selectedProfile;
    const nextProfile = pendingProfile;

    setIsUpdating(true);
    setSelectedProfile(nextProfile);
    onModeUpdate?.(device.id);

    try {
      await updateDeviceProfile(nextProfile, device);
      fetchDeviceList?.();
    } catch (error) {
      console.error("Failed to update profile:", error);
      setSelectedProfile(previousProfile);
    } finally {
      setIsUpdating(false);
      setPendingProfile(null);
    }
  };

  const handleCancelChange = () => {
    setPendingProfile(null);
  };

  const handleDialogOpenChange = (open: boolean) => {
    setShowConfirmDialog(open);
    if (!open) {
      setPendingProfile(null);
    }
  };

  const updateDeviceProfile = async (mode: string, device: any) => {
    const profile = profiles.find((el) => el.name === mode);

    if (profile) {
      try {
        await updateComputer(selectedTenant, device.id, profile.id);
        await new Promise(resolve => setTimeout(resolve, 45000));
        await updateProfile(selectedTenant, device.id, profile.id);
      } catch (e) {
        console.log("Error updating device profile: ", e);
        throw e;
      }

    }
  };

  if (device?.isManuallyAddedMachine) {
    return <span className="text-sm text-gray-500">Not Applicable</span>;
  }

  const getOptionsForOS = () => {
    const os = device?.os?.toLowerCase() || "";

    const osProfiles =
      profiles
        ?.filter((profile) => os.includes(profile.os?.toLowerCase()))
        .map((profile) => profile.name) || [];

    const defaultOptions =
      os.includes("windows")
        ? ["windows_original", "windows_secure", "windows_critical", "windows_normal"]
        : os.includes("mac") || os.includes("darwin")
          ? ["mac_original", "mac_secure", "mac_critical", "mac_normal"]
          : ["linux_original", "linux_secure", "linux_critical", "linux_normal"];

    const finalOptions = osProfiles.length > 0 ? osProfiles : defaultOptions;

    return finalOptions.map((opt) => (
      <SelectItem key={opt} value={opt}>
        {opt}
      </SelectItem>
    ));
  };


  return (
    <>
      <Select
        value={selectedProfile}
        onValueChange={handleModeChange}
        disabled={disabled || isUpdating}
      >
        <SelectTrigger className="h-8 text-xs w-36 border-border/70 bg-card shadow-[0_1px_3px_rgb(15_23_42/0.08)]">
          <SelectValue placeholder="Select mode" />
        </SelectTrigger>
        <SelectContent>{getOptionsForOS()}</SelectContent>
      </Select>

      <ConfirmationDialog
        open={showConfirmDialog}
        onOpenChange={handleDialogOpenChange}
        title="Confirm Profile Change"
        description={`Are you sure you want to change the profile to ${pendingProfile || "selected profile"}?`}
        confirmText="Update Profile"
        cancelText="Cancel"
        variant="warning"
        loading={isUpdating}
        onConfirm={handleConfirmChange}
        onCancel={handleCancelChange}
      />
    </>
  );
}
